/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from 'react';

import { useForm } from 'react-hook-form';

import CollapsableNotification from '../CollapsableNotification';
import FixedLengthField from '../FixedLengthField';
import Link from '../Link';

import {
  FixedLengthFieldContainer,
  FooterText,
  GuideText,
  GuideTextContainer,
  StyledButton,
  StyledVerification,
} from './index.styles';

interface FormValues {
  verification: string;
}

interface VerificationCodeProps {
  confirm: (data: FormValues) => void;
  resend?: () => void;
  onConfirm: () => void;
  // A string describing where the code was sent to
  source?: 'email' | 'mobile phone' | 'authenticator';
}

const VerificationCode = ({ confirm, resend, onConfirm, source } : VerificationCodeProps) => {
  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>();
  const [errorOpen, setErrorOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [cooldownTime, setCooldownTime] = useState(60);
  const [isCoolingDown, setIsCoolingDown] = useState(false);

  const onSuccess = () => {
    setLoading(false);
    onConfirm();
  };

  const onFailure = (error: any) => {
    setLoading(false);
    let message = error?.message;
    if (error?.code === 'CodeMismatchException') {
      message = 'Entered code is incorrect.';
    }
    setErrorMessage(message || 'An error has occurred');
    setErrorOpen(true);
  };

  const onSubmit = async (data: FormValues) => {
    setLoading(true);
    try {
      await confirm(data);
      onSuccess();
    } catch (error: any) {
      onFailure(error);
    }
  };

  const onResendCode = async () => {
    setLoading(true);
    try {
      if (resend) await resend();
      setLoading(false);
      setIsCoolingDown(true);
    } catch (error: any) {
      onFailure(error);
    }
  };

  useEffect(() => {
    if (isCoolingDown) {
      const interval = setInterval(() => {
        setCooldownTime((prevTime) => {
          if (prevTime === 0) {
            setIsCoolingDown(false);
            return 60;
          }
          return prevTime - 1;
        });
      }, 1000);
      return () => clearInterval(interval);
    }
    return () => {};
  }, [isCoolingDown]);

  return (
    <StyledVerification onSubmit={handleSubmit(onSubmit)}>
      <GuideTextContainer>
        <GuideText variant="h5">Please enter the code</GuideText>
        {source && (<GuideText variant="h5">{`sent to your ${source}`}</GuideText>)}
      </GuideTextContainer>
      <CollapsableNotification
        open={errorOpen}
        close={() => setErrorOpen(false)}
        message={errorMessage}
      />
      <FixedLengthFieldContainer>
        <FixedLengthField
          error={!!errors.verification}
          errorMessage=""
          maxLength={6}
          numbersOnly
          onFilled={(code) => onSubmit({ verification: code })}
          data-cy="verification"
          {...register('verification', { required: true, pattern: /\d{6}/ })}
        />
      </FixedLengthFieldContainer>
      <StyledButton variant="contained" type="submit" loading={loading}>
        Verify
      </StyledButton>
      {resend && (
        <FooterText>Can't find the code?&nbsp;
          {isCoolingDown ? ` (Resend in ${cooldownTime})` : (
            <Link
              component="button"
              variant="body1"
              onClick={onResendCode}
              underline="always"
              type="button"
            >
              Resend Code
            </Link>
          )}
        </FooterText>
      )}
    </StyledVerification>
  );
};

export default VerificationCode;
